import { message } from "antd";
import { type UploadChangeParam } from "antd/lib/upload";
import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@btnet/utilities-core";

import {
    BdsIconCheckCircle,
    BdsIconListChecks,
    BdsIconPlans,
    BdsIconSparkle,
    BdsIconUploadSimple,
    BdsText,
} from "@buildertrend/components";

import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes, ClientObservableBackgroundJobStatus, MediaType } from "types/enum";

import { reportError } from "utilities/errorHelpers";
import { getPdfPageCount } from "utilities/file/pdfPageCount";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTUploadRaw } from "commonComponents/btWrappers/BTUploadRaw/BTUploadRaw";
import { BrowseBTFiles } from "commonComponents/entity/media/BrowseBTFiles/BrowseBTFiles";
import { openBackgroundProcessStatusPopup } from "commonComponents/utilities/ClientObservableBackgroundJobStatusPopup/ClientObservableBackgroundJobStatusPopup";
import { ClientObservableBackgroundJobStatusPopupHandler } from "commonComponents/utilities/ClientObservableBackgroundJobStatusPopup/ClientObservableBackgroundJobStatusPopup.api.handler";
import { ProcessStatusGroupType } from "commonComponents/utilities/ClientObservableBackgroundJobStatusPopup/ClientObservableBackgroundJobStatusPopup.types";

import { type IPlanUploadItem } from "entity/Plans/PlansUploadModal/PlansUploadModal.types";
import { usePlansUpload } from "entity/Plans/PlansUploadModal/usePlansUpload";

import "./PlansEmptyState.less";

type LoaderStage = "uploading" | "analyzing" | "preparing" | "done";

interface IStageConfig {
    label: string;
    description: string;
    // text-* used by the center stage icon. Drives the per-stage icon color transition.
    // The ring itself uses a single blue (fg-info) — see the SVG below.
    iconColorClass: string;
}

const STAGE_CONFIG: Record<LoaderStage, IStageConfig> = {
    uploading: {
        label: "Uploading",
        description: "Transferring your files",
        iconColorClass: "text-fg-info",
    },
    analyzing: {
        label: "Analyzing plans",
        description: "Reading page names and sheet details",
        iconColorClass: "text-purple-50",
    },
    preparing: {
        label: "Preparing your workspace",
        description: "Finishing setup for your plans",
        iconColorClass: "text-teal-50",
    },
    done: {
        label: "Ready to collaborate",
        description: "Your plans are ready to review",
        iconColorClass: "text-fg-success",
    },
};

// Ring geometry. SVG viewBox is 120x120, radius 54 leaves room for the 6px stroke.
const RING_VIEWBOX = 120;
const RING_RADIUS = 54;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
const RING_STROKE_WIDTH = 6;

// Each stage owns an equal third of the ring's 0-100 progress range. stageProgress (0..1)
// within the current stage maps into its band so the single progress ring fills smoothly
// across all stages.
const STAGE_BOUNDS: Record<Exclude<LoaderStage, "done">, { start: number; end: number }> = {
    uploading: { start: 0, end: 100 / 3 },
    analyzing: { start: 100 / 3, end: 200 / 3 },
    preparing: { start: 200 / 3, end: 100 },
};

// Stage progression timing. Each visual stage fills its ring band over a duration that scales
// with the total page count of the uploaded PDFs, so a 2-page upload feels snappy and a 50-page
// upload feels appropriately deliberate. Preparing caps at 95% and sits there until BG
// processing actually finishes, so the snap to 100% on "done" reflects real completion.
const BASE_STAGE_DURATION_MS = 4000;
const MS_PER_PAGE = 900;
const MAX_STAGE_DURATION_MS = 20000;
const FALLBACK_PAGE_COUNT = 10;
const DONE_DISPLAY_MS = 4000;
const PREPARING_FILL_CAP = 0.95;
const DEFAULT_POLLING_RATE_SECONDS = 10;

function computeStageDuration(totalPages: number): number {
    return Math.min(MAX_STAGE_DURATION_MS, BASE_STAGE_DURATION_MS + totalPages * MS_PER_PAGE);
}

async function getItemPageCount(item: IPlanUploadItem): Promise<number> {
    const file = item.uploadFile?.originFileObj;
    if (!file) return FALLBACK_PAGE_COUNT;
    try {
        return await getPdfPageCount(file);
    } catch {
        // Unreadable PDF — fall back rather than blocking the upload on page counting.
        return FALLBACK_PAGE_COUNT;
    }
}

async function getTotalPageCount(items: IPlanUploadItem[]): Promise<number> {
    const counts = await Promise.all(items.map(getItemPageCount));
    return counts.reduce((a, b) => a + b, 0);
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function StageIcon({ stage }: { stage: LoaderStage }) {
    if (stage === "uploading") return <BdsIconUploadSimple size="xl" />;
    if (stage === "analyzing") return <BdsIconSparkle size="xl" />;
    if (stage === "preparing") return <BdsIconListChecks size="xl" />;
    return <BdsIconCheckCircle size="xl" />;
}

export interface IPlansEmptyStateProps {
    jobId: number | undefined;
    /**
     * Whether the current user can upload plans. Only builders with add permission on the
     * authenticated page can upload — subs, clients, and the external (share-link) page cannot.
     * When false, the read-only empty state is shown instead of the upload dropzone.
     */
    canUpload: boolean;
    /**
     * Invoked once all background processing has actually completed. Parent should refetch
     * the plans list — no need to open the BG popup, the in-page circle loader already showed
     * progress through completion.
     */
    onPlansReady: (backgroundJobIds: string[]) => void | Promise<void>;
    onError: (error: Error) => void;
}

export const PlansEmptyState = ({
    jobId,
    canUpload,
    onPlansReady,
    onError,
}: IPlansEmptyStateProps) => {
    const userContext = useContext(UserInfoContext);
    const appDefaults = useContext(AppDefaultInfoContext);
    const isBuilder = userContext?.loginType === BTLoginTypes.BUILDER;
    const pollingRateMs =
        (appDefaults?.importUIRefreshFrequency ?? DEFAULT_POLLING_RATE_SECONDS) * 1000;

    const [stage, setStage] = useState<LoaderStage | null>(null);
    // Progress 0..1 within the current stage's band. Drives the ring fill via raf.
    const [stageProgress, setStageProgress] = useState(0);
    const [showBrowseBTFiles, setShowBrowseBTFiles] = useState(false);

    // Used by the polling loop and post-await branches to abandon work when unmounted.
    const isUnmountedRef = useRef(false);
    useEffect(() => {
        return () => {
            isUnmountedRef.current = true;
        };
    }, []);

    // Per-upload stage duration in ms (scaled by total PDF page count). Set once in
    // handleStartUpload before any stage transition, then read by both the raf fill effect
    // and the inter-stage delay() awaits so they pace consistently.
    const stageDurationRef = useRef<number>(BASE_STAGE_DURATION_MS);

    // Guards against a second handleStartUpload firing during the brief async window before
    // setStage("uploading") swaps the dropzone out for the loader. Without this, a fast
    // double-drop / double-browse-select could kick off two parallel upload sequences and
    // clobber each other's state.
    const isStartingRef = useRef(false);

    const { uploadFiles, validateFile } = usePlansUpload({ jobId: jobId ?? 0 });

    // Fills the current stage's band linearly over stageDurationRef.current. Preparing caps at
    // 95% so the transition to "done" snaps to 100% deliberately when BG processing finishes.
    useEffect(() => {
        if (!stage) {
            setStageProgress(0);
            return;
        }
        if (stage === "done") {
            setStageProgress(1);
            return;
        }
        setStageProgress(0);
        const cap = stage === "preparing" ? PREPARING_FILL_CAP : 1;
        const durationMs = stageDurationRef.current;
        const startedAt = performance.now();
        let raf = 0;
        const tick = () => {
            const elapsed = performance.now() - startedAt;
            const t = Math.min(cap, elapsed / durationMs);
            setStageProgress(t);
            if (t < cap) {
                raf = requestAnimationFrame(tick);
            }
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [stage]);

    const ringProgress = useMemo(() => {
        if (!stage) return 0;
        if (stage === "done") return 100;
        const bounds = STAGE_BOUNDS[stage];
        return bounds.start + stageProgress * (bounds.end - bounds.start);
    }, [stage, stageProgress]);

    // Polls /api/ClientObservableBackgroundJobs until every tracked job has reached a terminal
    // status (Success | Failed | Deleted) or the component unmounts. Returns the failure count
    // (so the caller can decide messaging), or null if the component unmounted mid-poll.
    const waitForBackgroundJobs = async (jobIds: string[]): Promise<number | null> => {
        const handler = new ClientObservableBackgroundJobStatusPopupHandler();
        const trackedSet = new Set(jobIds);
        const terminalStatuses = new Set<ClientObservableBackgroundJobStatus>([
            ClientObservableBackgroundJobStatus.Success,
            ClientObservableBackgroundJobStatus.Failed,
            ClientObservableBackgroundJobStatus.Deleted,
        ]);

        while (!isUnmountedRef.current) {
            try {
                const result = await handler.getClientObservableBackgroundJobStatuses();
                if (isUnmountedRef.current) return null;
                const ours = result.backgroundJobStatuses.filter(
                    (b) =>
                        b.processType === ProcessStatusGroupType.PlanUpload &&
                        trackedSet.has(b.processID),
                );
                const respondedIds = new Set(ours.map((b) => b.processID));
                // Tracked jobs that disappeared from the active-jobs list are treated as
                // completed — the server clears jobs from the active list when they finish or
                // when another session dismissed the popup. Without this, a vanished job would
                // keep the polling loop running forever.
                const vanishedCount = jobIds.filter((id) => !respondedIds.has(id)).length;
                const terminalCount = ours.filter((b) => terminalStatuses.has(b.status)).length;
                if (vanishedCount + terminalCount === jobIds.length) {
                    return ours.filter(
                        (b) =>
                            b.status === ClientObservableBackgroundJobStatus.Failed ||
                            b.status === ClientObservableBackgroundJobStatus.Deleted,
                    ).length;
                }
            } catch (e) {
                reportError(e, {
                    internalNotes: `Polling background job statuses failed for plans empty-state loader, jobId: ${
                        jobId ?? "unknown"
                    }`,
                });
            }
            await delay(pollingRateMs);
        }
        return null;
    };

    // When the user navigates away before the upload's sync portion finishes, await the upload
    // anyway and open the popup once the BG job is registered so they retain visibility.
    const handoffOnUnmount = async (
        pendingUpload: ReturnType<typeof uploadFiles>,
    ): Promise<void> => {
        try {
            const { backgroundJobIds } = await pendingUpload;
            const asyncJobIds = backgroundJobIds.filter((id) => id && id.length > 0);
            if (asyncJobIds.length > 0) {
                openBackgroundProcessStatusPopup(ProcessStatusGroupType.PlanUpload);
            }
        } catch (e) {
            reportError(e, {
                internalNotes: `Plan upload failed after empty-state unmount for jobId: ${
                    jobId ?? "unknown"
                }`,
            });
        }
    };

    const handleStartUpload = async (items: IPlanUploadItem[]) => {
        if (items.length === 0 || jobId === undefined) return;
        if (isStartingRef.current) return;
        isStartingRef.current = true;
        // Kick off the real upload immediately so network time is parallel to page counting.
        const uploadPromise = uploadFiles(items);
        // Watcher that propagates uploadPromise rejection but never resolves on success — used
        // to short-circuit the decorative stage delays so a fast-failing network error surfaces
        // immediately instead of waiting through 8–40s of animation.
        const uploadErrorWatcher = uploadPromise.then<never>(
            () => new Promise<never>(() => {}),
            (err) => {
                throw err;
            },
        );
        const racedDelay = (ms: number) => Promise.race([delay(ms), uploadErrorWatcher]);

        try {
            // Size the decorative animation to the upload — more pages → longer loader.
            const totalPages = await getTotalPageCount(items);
            stageDurationRef.current = computeStageDuration(totalPages);
            if (isUnmountedRef.current) return handoffOnUnmount(uploadPromise);
            setStage("uploading");

            // Decorative stage progression on the page-count-scaled timer. Each delay races
            // against the upload's error path so we bail out early on failure.
            await racedDelay(stageDurationRef.current);
            if (isUnmountedRef.current) return handoffOnUnmount(uploadPromise);
            setStage("analyzing");
            await racedDelay(stageDurationRef.current);
            if (isUnmountedRef.current) return handoffOnUnmount(uploadPromise);
            setStage("preparing");
            const preparingStartedAt = performance.now();
            const preparingMinDurationMs = stageDurationRef.current;

            // Wait for the real upload's sync portion (temp-upload + plansProcessPostV2).
            const { backgroundJobIds, errors } = await uploadPromise;
            const asyncJobIds = backgroundJobIds.filter((id) => id && id.length > 0);

            if (isUnmountedRef.current) {
                if (asyncJobIds.length > 0) {
                    openBackgroundProcessStatusPopup(ProcessStatusGroupType.PlanUpload);
                }
                return;
            }

            if (errors.size > 0) {
                errors.forEach((errorMessage, itemId) => {
                    const item = items.find((i) => i.id === itemId);
                    const fileName = item?.fileName ?? "Unknown file";
                    message.error(`Failed to upload ${fileName}: ${errorMessage}`);
                });
                // No usable BG jobs to wait on (either none returned, or all returned IDs were
                // empty strings) — reset to the dropzone rather than falsely showing success.
                if (asyncJobIds.length === 0) {
                    setStage(null);
                    return;
                }
            }

            if (asyncJobIds.length > 0) {
                // Sit in "preparing" — ring stays at the 95% cap — until BG processing finishes.
                const failedCount = await waitForBackgroundJobs(asyncJobIds);
                if (failedCount === null) {
                    // Component unmounted during polling. Hand off to the popup so the user
                    // can still see their work complete.
                    openBackgroundProcessStatusPopup(ProcessStatusGroupType.PlanUpload);
                    return;
                }
                if (failedCount === asyncJobIds.length) {
                    message.error("File processing failed. Please try again.");
                    setStage(null);
                    return;
                }
                if (failedCount > 0) {
                    message.warning(
                        `${failedCount} file(s) failed during processing. ${
                            asyncJobIds.length - failedCount
                        } succeeded.`,
                    );
                }
            }

            // Hold "preparing" for at least its minimum duration so the ring has time to
            // fill on really small files where upload + BG processing finish before the
            // animation does. Without this, preparing flashes by and the ring jumps from
            // ~66% straight to 100% on "done".
            const elapsedInPreparing = performance.now() - preparingStartedAt;
            if (elapsedInPreparing < preparingMinDurationMs) {
                await delay(preparingMinDurationMs - elapsedInPreparing);
                if (isUnmountedRef.current) return;
            }

            setStage("done");
            await delay(DONE_DISPLAY_MS);
            if (isUnmountedRef.current) return;
            await onPlansReady(backgroundJobIds);
            // Parent's refetch returns plans → it unmounts us. If for some reason it doesn't,
            // fall back to the dropzone so the user isn't stuck on "done".
            setStage(null);
        } catch (error) {
            reportError(error, {
                internalNotes: `Plan upload from empty state failed for jobId: ${jobId}`,
            });
            onError(error instanceof Error ? error : new Error("Upload failed"));
            if (!isUnmountedRef.current) setStage(null);
        } finally {
            isStartingRef.current = false;
        }
    };

    const handleFileListChange = (info: UploadChangeParam) => {
        const items: IPlanUploadItem[] = [];
        info.fileList.forEach((uploadFile) => {
            const file = uploadFile.originFileObj;
            if (!file) return;
            const validation = validateFile(file, true);
            if (!validation.isValid && validation.error) {
                message.error(`${file.name}: ${validation.error}`);
                return;
            }
            items.push({
                id: uploadFile.uid,
                fileName: file.name,
                fileSize: file.size,
                uploadFile: { ...uploadFile, status: "done" as const },
            });
        });
        if (items.length > 0) {
            void handleStartUpload(items);
        }
    };

    const handleOpenBrowseBTFiles = (event: React.MouseEvent) => {
        event.stopPropagation();
        setShowBrowseBTFiles(true);
    };

    const handleCloseBrowseBTFiles = () => {
        setShowBrowseBTFiles(false);
    };

    // eslint-disable-next-line @typescript-eslint/require-await
    const handleBTFilesSelected = async (files: BTFileSystem[]) => {
        setShowBrowseBTFiles(false);
        const pdfFiles = files.filter((file) => {
            const ext = file.extension?.toLowerCase().replace(".", "");
            return ext === "pdf";
        });
        if (pdfFiles.length === 0) {
            message.warning("No PDF files selected. Only PDF files can be uploaded to Plans.");
            return;
        }
        if (pdfFiles.length < files.length) {
            message.warning(
                `${
                    files.length - pdfFiles.length
                } non-PDF file(s) were excluded. Only PDF files can be uploaded to Plans.`,
            );
        }
        const items: IPlanUploadItem[] = [];
        pdfFiles.forEach((btFile) => {
            if (!btFile.documentInstanceId) return;
            const validation = validateFile(btFile, false);
            if (!validation.isValid && validation.error) {
                message.error(`${btFile.title}: ${validation.error}`);
                return;
            }
            items.push({
                id: `bt-${btFile.id}`,
                fileName: btFile.title,
                fileSize: btFile.fileSize,
                documentInstanceId: btFile.id,
                extension: btFile.extension,
            });
        });
        if (items.length > 0) {
            void handleStartUpload(items);
        }
    };

    // Read-only empty state for users who can't upload (subs, clients, and the external
    // share-link page). They get the simple "no plans" message rather than the upload dropzone.
    if (!canUpload) {
        return (
            <div className="flex flex-col gap-6 plans-empty-state" data-testid="plans-empty-state">
                <BdsIconPlans size="3xl" className="EmptyStateIcon" testid="EmptyStateIcon" />
                <div className="flex flex-col items-center">
                    <BdsText size="distinct-lg" text="Add a plan" />
                    {isBuilder ? (
                        <BdsText
                            className="plans-body-text"
                            size="normal-lg"
                            text="Upload blueprint plans to keep drawings, layouts, and revisions in one place. Your crew can view and reference them anytime."
                        />
                    ) : (
                        <BdsText
                            className="plans-body-text"
                            size="normal-lg"
                            text="Your builder hasn't uploaded any plans for this project yet"
                        />
                    )}
                </div>
            </div>
        );
    }

    if (stage) {
        const config = STAGE_CONFIG[stage];
        const dashOffset = RING_CIRCUMFERENCE * (1 - ringProgress / 100);

        return (
            <div
                className="PlansEmptyState-Card PlansEmptyState-Loader"
                data-testid="plans-empty-state-loader"
            >
                <div className="flex w-full flex-col items-center gap-8 text-center">
                    <div className="relative size-30 flex items-center justify-center">
                        <svg
                            className="PlansEmptyStateLoaderRing"
                            viewBox={`0 0 ${RING_VIEWBOX} ${RING_VIEWBOX}`}
                            width={RING_VIEWBOX}
                            height={RING_VIEWBOX}
                        >
                            <circle
                                cx={RING_VIEWBOX / 2}
                                cy={RING_VIEWBOX / 2}
                                r={RING_RADIUS}
                                fill="none"
                                strokeWidth={RING_STROKE_WIDTH}
                                className="stroke-gray-15"
                            />
                            <circle
                                cx={RING_VIEWBOX / 2}
                                cy={RING_VIEWBOX / 2}
                                r={RING_RADIUS}
                                fill="none"
                                strokeWidth={RING_STROKE_WIDTH}
                                strokeLinecap="round"
                                strokeDasharray={RING_CIRCUMFERENCE}
                                strokeDashoffset={dashOffset}
                                className="stroke-fg-info"
                            />
                        </svg>
                        <div
                            key={stage}
                            className={cn("PlansEmptyStateIcon relative", config.iconColorClass)}
                        >
                            <StageIcon stage={stage} />
                        </div>
                    </div>
                    <div
                        key={`text-${stage}`}
                        className="PlansEmptyStateStageText flex flex-col items-center gap-2"
                    >
                        <BdsText size="distinct-lg" text={config.label} />
                        <BdsText
                            size="normal-md"
                            className="text-fg-secondary"
                            text={config.description}
                        />
                        <BdsText
                            size="normal-lg"
                            className="mt-1 text-fg-default font-medium"
                            text={`${Math.round(ringProgress)}%`}
                        />
                    </div>
                </div>
            </div>
        );
    }

    const dropzoneCopy =
        "Upload blueprint plans to keep drawings, layouts, and revisions in one place. Your crew can view and reference them anytime.";

    const dropzone = (
        <div
            className="PlansEmptyState-Card PlansEmptyState-Dropzone"
            data-testid="plans-empty-state"
        >
            <div className="flex w-full flex-col items-center gap-6 text-center pointer-events-none">
                <div className="flex size-16 items-center justify-center text-fg-secondary">
                    <BdsIconPlans size="3xl" testid="EmptyStateIcon" />
                </div>
                <div className="flex flex-col items-center gap-1">
                    <BdsText size="distinct-lg" text="Add a plan" />
                    <BdsText
                        className="PlansEmptyState-DropzoneCopy text-fg-secondary"
                        size="normal-lg"
                        text={dropzoneCopy}
                    />
                </div>
            </div>
            {/*
             * BTUploadRaw fires the file picker on mousedown anywhere in the dropzone.
             * Stop mousedown here so the buttons' focus/active visuals work normally;
             * Browse device's click still bubbles up to trigger the picker, while
             * Browse Buildertrend uses stopPropagation in its onClick to open its modal.
             */}
            <div
                className="flex flex-wrap items-center justify-center gap-2"
                onMouseDown={(e) => e.stopPropagation()}
            >
                <BTButton data-testid="plans-empty-browse-computer" type="secondary">
                    Browse device
                </BTButton>
                <BTButton
                    data-testid="plans-empty-browse-buildertrend"
                    type="secondary"
                    onClick={handleOpenBrowseBTFiles}
                >
                    Browse Buildertrend
                </BTButton>
            </div>
        </div>
    );

    return (
        <>
            <BTUploadRaw
                id="plans-empty-state-upload"
                data-testid="plans-empty-state-upload"
                isArea
                fillContainer
                multiple
                mediaType={MediaType.Document}
                allowedFileTypes={["pdf"]}
                value={[]}
                uploadConfig={{ shouldUploadFullResolutionPhoto: false }}
                showUploadList={false}
                openFileDialogOnClick={true}
                onChange={handleFileListChange}
                onRemove={() => {}}
                beforeUpload={() => Promise.resolve(false)}
            >
                {dropzone}
            </BTUploadRaw>
            {showBrowseBTFiles && jobId !== undefined && (
                <BrowseBTFiles
                    jobId={jobId}
                    allowedFileTypes={["pdf"]}
                    allowedMediaTypes={[MediaType.Document]}
                    multiple={true}
                    onCancel={handleCloseBrowseBTFiles}
                    onSubmit={handleBTFilesSelected}
                    modalConfig={{
                        parentRoute: "",
                        beforeClose: handleCloseBrowseBTFiles,
                    }}
                />
            )}
        </>
    );
};
